import React, { useState } from 'react';
import { useCRM } from '../context/CRMContext';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  Mail, 
  Phone, 
  Building,
  DollarSign,
  Calendar,
  Target,
  Users,
  Globe,
  AlertCircle
} from 'lucide-react';

const SalesLeads = () => {
  const { salesLeads, addSalesLead, updateSalesLead, deleteSalesLead } = useCRM();
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    industry: '',
    companySize: '',
    revenue: '',
    status: 'new',
    priority: 'medium',
    dealValue: '',
    notes: '',
    linkedinUrl: '',
    lastContact: '',
    nextFollowUp: ''
  });

  const statuses = [
    { value: 'new', label: 'New Lead', color: 'bg-blue-100 text-blue-800' },
    { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'qualified', label: 'Qualified', color: 'bg-green-100 text-green-800' },
    { value: 'demo_scheduled', label: 'Demo Scheduled', color: 'bg-purple-100 text-purple-800' },
    { value: 'proposal_sent', label: 'Proposal Sent', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'negotiating', label: 'Negotiating', color: 'bg-orange-100 text-orange-800' },
    { value: 'closed_won', label: 'Closed Won', color: 'bg-green-200 text-green-900' },
    { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-800' },
    { value: 'on_hold', label: 'On Hold', color: 'bg-gray-100 text-gray-800' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-800' },
    { value: 'high', label: 'High', color: 'bg-red-100 text-red-800' }
  ];

  const companySizes = [
    { value: 'startup', label: 'Startup (1-10)' },
    { value: 'small', label: 'Small (11-50)' },
    { value: 'medium', label: 'Medium (51-200)' },
    { value: 'large', label: 'Large (201-1000)' },
    { value: 'enterprise', label: 'Enterprise (1000+)' }
  ];

  const industries = [
    'SaaS/Software', 'E-commerce', 'Fintech', 'Healthcare', 'Education',
    'Marketing', 'Real Estate', 'Manufacturing', 'Consulting', 'Other'
  ];

  const resetForm = () => {
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      website: '',
      industry: '',
      companySize: '',
      revenue: '',
      status: 'new',
      priority: 'medium',
      dealValue: '',
      notes: '',
      linkedinUrl: '',
      lastContact: '',
      nextFollowUp: ''
    });
    setEditingLead(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingLead) {
      updateSalesLead(editingLead.id, formData);
    } else {
      addSalesLead(formData);
    }
    setShowModal(false);
    resetForm();
  };

  const handleEdit = (lead) => {
    setFormData(lead);
    setEditingLead(lead);
    setShowModal(true);
  };

  const filteredLeads = salesLeads.filter(lead => {
    const matchesSearch = lead.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.contactName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || lead.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusInfo = (status) => statuses.find(s => s.value === status) || statuses[0];
  const getPriorityInfo = (priority) => priorities.find(p => p.value === priority) || priorities[1];

  const totalPipelineValue = filteredLeads
    .filter(lead => lead.dealValue && lead.status !== 'closed_lost')
    .reduce((sum, lead) => sum + parseFloat(lead.dealValue.replace(/[^0-9.-]+/g, '') || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Leads</h1>
          <p className="text-gray-600 mt-2">Track potential customers and grow your revenue pipeline</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Lead</span>
        </button>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-50">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-2xl font-semibold text-gray-900">{filteredLeads.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-50">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Qualified</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredLeads.filter(l => ['qualified', 'demo_scheduled', 'proposal_sent', 'negotiating'].includes(l.status)).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-50">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pipeline Value</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${totalPipelineValue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search companies or contacts..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field"
          >
            <option value="all">All Status</option>
            {statuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="input-field"
          >
            <option value="all">All Priorities</option>
            {priorities.map(priority => (
              <option key={priority.value} value={priority.value}>{priority.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Leads Grid */}
      {filteredLeads.length === 0 ? (
        <div className="card text-center py-12">
          <Target className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {salesLeads.length === 0 ? "Get started by adding your first sales lead." : "Try adjusting your search or filters."}
          </p>
          <div className="mt-6">
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Lead
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredLeads.map((lead) => {
            const statusInfo = getStatusInfo(lead.status);
            const priorityInfo = getPriorityInfo(lead.priority);
            const sizeInfo = companySizes.find(s => s.value === lead.companySize);
            
            return (
              <div key={lead.id} className="card hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{lead.companyName}</h3>
                    <p className="text-gray-600">{lead.contactName}</p>
                    {lead.industry && (
                      <p className="text-sm text-gray-500 mt-1">{lead.industry}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(lead)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      onClick={() => deleteSalesLead(lead.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${priorityInfo.color}`}>
                      {priorityInfo.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {sizeInfo && (
                      <div className="flex items-center text-gray-600">
                        <Building className="mr-2 h-4 w-4" />
                        {sizeInfo.label.split(' ')[0]}
                      </div>
                    )}
                    {lead.dealValue && (
                      <div className="flex items-center text-gray-600">
                        <DollarSign className="mr-2 h-4 w-4" />
                        {lead.dealValue}
                      </div>
                    )}
                  </div>

                  {lead.nextFollowUp && (
                    <div className="flex items-center text-sm text-orange-600 bg-orange-50 rounded-lg p-2">
                      <AlertCircle className="mr-2 h-4 w-4" />
                      Follow up: {new Date(lead.nextFollowUp).toLocaleDateString()}
                    </div>
                  )}

                  <div className="flex space-x-2 pt-3 border-t border-gray-100">
                    {lead.email && (
                      <a
                        href={`mailto:${lead.email}`}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Mail size={16} />
                      </a>
                    )}
                    {lead.phone && (
                      <a
                        href={`tel:${lead.phone}`}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                      >
                        <Phone size={16} />
                      </a>
                    )}
                    {lead.website && (
                      <a
                        href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Globe size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-6">
                {editingLead ? 'Edit Sales Lead' : 'Add New Sales Lead'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Company Name *"
                    className="input-field"
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Contact Name"
                    className="input-field"
                    value={formData.contactName}
                    onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    placeholder="Email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    className="input-field"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="url"
                    placeholder="Website"
                    className="input-field"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                  />
                  <select
                    className="input-field"
                    value={formData.industry}
                    onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  >
                    <option value="">Select Industry</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    className="input-field"
                    value={formData.companySize}
                    onChange={(e) => setFormData({...formData, companySize: e.target.value})}
                  >
                    <option value="">Company Size</option>
                    {companySizes.map(size => (
                      <option key={size.value} value={size.value}>{size.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Deal Value (e.g., $5,000)"
                    className="input-field"
                    value={formData.dealValue}
                    onChange={(e) => setFormData({...formData, dealValue: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    className="input-field"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  <select
                    className="input-field"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>{priority.label}</option>
                    ))}
                  </select>
                </div>

                <input
                  type="date"
                  placeholder="Next Follow-up Date"
                  className="input-field"
                  value={formData.nextFollowUp}
                  onChange={(e) => setFormData({...formData, nextFollowUp: e.target.value})}
                />

                <textarea
                  placeholder="Notes (pain points, requirements, meeting notes...)"
                  className="input-field h-24 resize-none"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingLead ? 'Update' : 'Add'} Lead
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesLeads;